import {Alert, Group, Modal, Stack, Text} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import type {ReactNode} from 'react'
import {useMemo} from 'react'

import {ConfirmOutPanel} from 'src/rentals/confirm-out-panel'
import {OutOfStateBadge} from 'src/ui/out-of-state-badge'

import {useCurrentUser} from '../auth/use-current-user'
import type {DispatchYard} from '../dispatch/schemas'
import {otherYardJobNote} from '../dispatch/yards'
import {
    canMoveBetweenYards,
    canPublish,
    isOtherYardJob,
    ownsAsRep,
    ownsWork,
    viewOnlyNote,
} from '../jobs/edit-rights'
import {
    formatDateTbcLabel,
    formatJobScheduleRange,
    toBusinessApiDateString,
} from '../jobs/job-schedule-format'
import {ConfirmPickupButton} from '../work/confirm-pickup-button'
import {PartialPickupBadge} from '../work/partial-pickup'
import {PickupReadyCheck} from '../work/pickup-ready-check'
import {persistedJobIdFromRow, timelineRowToBoardJob} from './board-job-utils'
import {DateTbcPanel} from './date-tbc-panel'
import {DidNotHappenPanel} from './did-not-happen-panel'
import {DispatchAssignedResourcesPanel, DispatchAssignmentPanel} from './dispatch-assignment-panel'
import classes from './dispatch-drawer.module.css'
import {buildDrawerBoardContext, buildIdentityScheduleLabel} from './dispatch-drawer-briefing'
import {DrawerJobIdentity} from './dispatch-drawer-identity'
import {DrawerLaneHint, DrawerOperationalSectionHeading} from './dispatch-drawer-typography'
import type {TimelineJobRow} from './job-rows'
import {JobYardPanel} from './job-yard-panel'
import {isPastEndDate, missingEquipmentCount} from './past-end-date'
import {PastEndDatePanel} from './past-end-date-panel'
import {CloserYardTag, NO_YARD_WARNING} from './terminal-row'
import type {DispatchTimelineData, JobAllocation, JobRequirement, ViewRange} from './types'

type DispatchJobModalProps = {
    opened: boolean
    row: TimelineJobRow | null
    canonicalData: DispatchTimelineData
    displayData: DispatchTimelineData
    viewRange: ViewRange
    onClose: () => void
    onSubmit: (allocations: JobAllocation[]) => Promise<void>
    onReplaceAllocations: (allocations: JobAllocation[]) => Promise<void>
    onApplyStockDelta: (requirement: JobRequirement, delta: number) => Promise<void>
    onCopyDriverLink: (workerId: string) => Promise<void>
    onMarkDriverSent: (resourceId: string) => Promise<void>
    onOpenBoardJob?: (jobId: string) => void
    onExtendEndDate?: (jobId: string, endDate: string) => Promise<void>
    onMarkCompleted?: (jobId: string) => Promise<void>
    yards?: DispatchYard[]
    onSetProjectYard?: (projectId: string, yardId: string) => Promise<void>
    onSetJobDate?: (jobId: string, date: string) => Promise<void>
    onDidNotHappen?: (jobId: string, note: string) => Promise<void>
    onPickupCompleted?: () => Promise<void>
    onPickupConfirmed?: () => void
    onRentalsConfirmed?: () => void
}

/** Hide the seeded catch-all service type so Job Details does not lead with "Other". */
function meaningfulServiceTypeName(name: string | null | undefined): string | undefined {
    const trimmed = name?.trim()
    if (!trimmed || trimmed.toLowerCase() === 'other') {
        return undefined
    }
    return trimmed
}

function OperationalSection({
    title,
    testId,
    first = false,
    headerTrailing,
    children,
}: {
    title: string
    testId: string
    first?: boolean
    headerTrailing?: ReactNode
    children: ReactNode
}) {
    return (
        <section
            className={first ? classes['operationalSectionFirst'] : classes['operationalSection']}
            data-testid={testId}
        >
            <Group justify="flex-start" align="baseline" wrap="nowrap" gap="sm">
                <DrawerOperationalSectionHeading>{title}</DrawerOperationalSectionHeading>
                {headerTrailing}
            </Group>
            {children}
        </section>
    )
}

export function DispatchJobModal({
    opened,
    row,
    canonicalData,
    displayData,
    viewRange,
    onClose,
    onSubmit,
    onReplaceAllocations,
    onApplyStockDelta,
    onCopyDriverLink,
    onMarkDriverSent,
    onOpenBoardJob,
    onExtendEndDate,
    onMarkCompleted,
    yards = [],
    onSetProjectYard,
    onSetJobDate,
    onDidNotHappen,
    onPickupCompleted,
    onPickupConfirmed,
    onRentalsConfirmed,
}: DispatchJobModalProps) {
    const currentUser = useCurrentUser()
    const userYardId = currentUser?.dispatchYardId ?? null
    const boardJob = useMemo(
        () => (row ? timelineRowToBoardJob(row, canonicalData) : null),
        [row, canonicalData],
    )

    const boardContext = useMemo(
        () => (row ? buildDrawerBoardContext(row, displayData, viewRange) : null),
        [row, displayData, viewRange],
    )

    const jobId = row ? persistedJobIdFromRow(row) : null
    const identity = jobId != null ? canonicalData.jobIdentities?.[jobId] : undefined

    if (!row || !boardContext) {
        return null
    }

    const customerLabel = identity?.customerName ?? row.customer
    const jobCode = identity?.displayId ?? row.jobCode
    const absoluteScheduleLabel = identity?.dateTbc
        ? formatDateTbcLabel(identity.startTime)
        : identity
          ? formatJobScheduleRange({
                startDate: identity.startDate,
                endDate: identity.endDate === identity.startDate ? null : identity.endDate,
                startTime: identity.startTime,
                endTime: null,
                style: 'compact',
            })
          : undefined

    const identitySchedule = buildIdentityScheduleLabel(boardContext, absoluteScheduleLabel)
    const jobYardId = identity?.dispatchYardId ?? boardJob?.dispatchYardId ?? null
    const otherRepsJob = identity != null && !ownsWork(currentUser, identity)
    const otherYardJob =
        isOtherYardJob(currentUser, jobYardId) &&
        !(identity != null && ownsAsRep(currentUser, identity))
    const readOnly = otherYardJob || otherRepsJob
    const readOnlyNote = otherRepsJob
        ? viewOnlyNote(identity?.repName, 'job')
        : otherYardJob
          ? otherYardJobNote(
                identity?.dispatchYardName ??
                    yards.find((yard) => yard.id === jobYardId)?.name ??
                    null,
                yards.find((yard) => yard.id === userYardId)?.name ?? null,
            )
          : null
    const showAssignControls = boardContext.showAssignControls && !readOnly
    const notDueYet =
        identity != null &&
        (identity.dateTbc === true || identity.startDate > toBusinessApiDateString(new Date()))
    const siteName = identity?.siteName ?? undefined
    const projectName = identity?.projectName
    const description = identity?.description?.trim() || undefined
    const serviceType = meaningfulServiceTypeName(identity?.serviceTypeName)

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="960px"
            centered
            data-testid="dispatch-job-modal"
            title={
                <div className={classes['modalTitleBlock']}>
                    <span className={classes['drawerJobTitle']}>{row.label}</span>
                    <DrawerLaneHint
                        lane={boardContext.lane}
                        detail={boardContext.radioStatus.line}
                    />
                    {identity?.closerYardName || identity?.outOfStateLabel ? (
                        <div>
                            {identity.closerYardName ? (
                                <CloserYardTag yardName={identity.closerYardName} />
                            ) : null}{' '}
                            {identity.closerYardName && identity.closerYardReason ? (
                                <Text span size="xs" c="dimmed">
                                    {identity.closerYardReason}
                                </Text>
                            ) : null}{' '}
                            <OutOfStateBadge label={identity.outOfStateLabel} />
                        </div>
                    ) : null}
                    {identity && identity.dispatchYardId === null ? (
                        <Text size="xs" c="yellow.8" data-testid="no-yard-warning">
                            {NO_YARD_WARNING}
                        </Text>
                    ) : null}
                </div>
            }
            withCloseButton
            padding="xl"
            classNames={{
                header: classes['modalHeader'],
                body: classes['modalBody'],
                close: classes['modalClose'],
            }}
            styles={{
                header: {
                    marginBottom: 0,
                    paddingTop: 'var(--mantine-spacing-lg)',
                    paddingBottom: 'var(--mantine-spacing-sm)',
                    borderBottom: '1px solid var(--mantine-color-gray-2)',
                },
                body: {
                    paddingTop: 0,
                },
            }}
        >
            <Stack gap="md">
                {readOnlyNote ? (
                    <Alert color="gray" variant="light" data-testid="other-yard-read-only">
                        {readOnlyNote}
                    </Alert>
                ) : null}

                <OperationalSection title="Job Details" testId="drawer-identity-section" first>
                    <DrawerJobIdentity
                        customer={
                            identity?.customerId && customerLabel ? (
                                <Link
                                    to="/customers/$customerId"
                                    params={{customerId: identity.customerId}}
                                    className={classes['identityLink']}
                                    onClick={onClose}
                                >
                                    {customerLabel}
                                </Link>
                            ) : (
                                customerLabel
                            )
                        }
                        projectName={
                            identity?.projectId && projectName ? (
                                <Link
                                    to="/projects/$projectId"
                                    params={{projectId: identity.projectId}}
                                    className={classes['identityLink']}
                                    onClick={onClose}
                                >
                                    {projectName}
                                </Link>
                            ) : (
                                projectName
                            )
                        }
                        internalContactName={
                            boardJob?.internalContactName ?? row.internalContactName ?? null
                        }
                        description={description}
                        serviceType={serviceType}
                        scheduleLabel={identitySchedule}
                        jobCode={
                            identity && jobId && jobCode ? (
                                <Link
                                    to="/jobs/$jobId"
                                    params={{jobId}}
                                    className={classes['identityLink']}
                                    onClick={onClose}
                                >
                                    {jobCode}
                                </Link>
                            ) : (
                                jobCode
                            )
                        }
                        siteName={siteName}
                        layout="grid"
                    />
                </OperationalSection>

                <PartialPickupBadge partialPickup={identity?.partialPickup} />

                {jobId && identity ? (
                    <DidNotHappenPanel
                        reportedAt={identity.didNotHappenAt}
                        onReport={
                            onDidNotHappen &&
                            !readOnly &&
                            !notDueYet &&
                            canPublish(currentUser, jobYardId)
                                ? (note) => onDidNotHappen(jobId, note)
                                : undefined
                        }
                    />
                ) : null}

                {jobId && identity?.dateTbc && onSetJobDate && !readOnly ? (
                    <DateTbcPanel onSetDate={(date) => onSetJobDate(jobId, date)} />
                ) : null}

                {jobId && identity?.isPickup ? (
                    <PickupReadyCheck
                        projectId={identity.projectId}
                        jobId={jobId}
                        startDate={identity.startDate}
                        dateTbc={identity.dateTbc ?? false}
                        confirmedAt={identity.pickupConfirmedAt ?? null}
                        confirmedByName={null}
                        canConfirm={!readOnly}
                        onConfirmed={onPickupConfirmed}
                    />
                ) : null}

                <ConfirmOutPanel
                    rentals={identity?.unconfirmedRentals ?? []}
                    canConfirm={canPublish(currentUser, jobYardId)}
                    onConfirmed={onRentalsConfirmed}
                />

                {jobId && identity?.isPickup && onPickupCompleted && !readOnly ? (
                    <Group justify="space-between" data-testid="drawer-pickup">
                        <Text size="sm">Pickup from existing order</Text>
                        <ConfirmPickupButton
                            projectId={identity.projectId}
                            jobId={jobId}
                            onConfirmed={() => void onPickupCompleted()}
                        />
                    </Group>
                ) : null}

                {identity?.driverOwnedTruckTypes?.length ? (
                    <Text size="sm" data-testid="driver-owned-trucks">
                        Driver brings: <strong>{identity.driverOwnedTruckTypes.join(', ')}</strong>
                    </Text>
                ) : null}

                {identity?.onSiteEquipmentTypes?.length ? (
                    <Text size="sm" data-testid="on-site-equipment">
                        Already on site: <strong>{identity.onSiteEquipmentTypes.join(', ')}</strong>
                    </Text>
                ) : null}

                {identity &&
                yards.length > 0 &&
                onSetProjectYard &&
                (!readOnly || (otherYardJob && canMoveBetweenYards(currentUser))) ? (
                    <JobYardPanel
                        yardName={identity.dispatchYardName}
                        yards={yards}
                        canMove={canMoveBetweenYards(currentUser)}
                        onSetYard={(yardId) => onSetProjectYard(identity.projectId, yardId)}
                    />
                ) : null}

                {jobId &&
                identity &&
                isPastEndDate(identity.endDate) &&
                onExtendEndDate &&
                onMarkCompleted &&
                !readOnly ? (
                    <PastEndDatePanel
                        endDate={identity.endDate!}
                        missingEquipmentCount={boardJob ? missingEquipmentCount(boardJob) : 0}
                        onExtendEndDate={(endDate) => onExtendEndDate(jobId, endDate)}
                        onMarkCompleted={() => onMarkCompleted(jobId)}
                    />
                ) : null}

                {boardJob ? (
                    showAssignControls ? (
                        <OperationalSection title="Crew & Equipment" testId="drawer-resolution">
                            <DispatchAssignmentPanel
                                active={opened}
                                boardJob={boardJob}
                                bookByDay={identity?.bookByDay === true}
                                oneDay={identity?.isPickup === true}
                                viewedDay={canonicalData.scheduleWindowStart ?? null}
                                displayData={canonicalData}
                                onSubmit={onSubmit}
                                onReplaceAllocations={onReplaceAllocations}
                                onApplyStockDelta={onApplyStockDelta}
                                onMarkDriverSent={onMarkDriverSent}
                                onCopyDriverLink={onCopyDriverLink}
                                onOpenBoardJob={onOpenBoardJob}
                            />
                        </OperationalSection>
                    ) : (
                        <OperationalSection title="Crew & Equipment" testId="drawer-crew">
                            <DispatchAssignedResourcesPanel
                                boardJob={boardJob}
                                displayData={displayData}
                            />
                        </OperationalSection>
                    )
                ) : null}
            </Stack>
        </Modal>
    )
}
